import React from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const stats = [
  { value: "24/7", label: "Threat Visibility" },
  { value: "100+", label: "Security Controls Reviewed" },
  { value: "Real-Time", label: "Monitoring Support" },
  { value: "Business-Ready", label: "Security Approach" },
];

const services = [
  {
    title: "Application Security",
    desc: "Protect web applications, APIs, and internal platforms from common attack paths and implementation weaknesses before they affect users or operations.",
    points: [
      "Input validation and sanitization",
      "Authentication and session security",
      "Secure API design and protection",
      "Vulnerability review and mitigation",
    ],
    icon: "🛡️",
  },
  {
    title: "Data Protection",
    desc: "Protect sensitive operational, customer, and business records through stronger handling, access management, and security controls across systems.",
    points: [
      "Data encryption strategy",
      "Secure storage practices",
      "Controlled access permissions",
      "Safer handling of sensitive records",
    ],
    icon: "🔐",
  },
  {
    title: "Threat Monitoring",
    desc: "Improve visibility into suspicious behavior, risky activity, and system anomalies so teams can respond faster and reduce exposure.",
    points: [
      "Real-time alerting",
      "Log monitoring and visibility",
      "Anomaly detection support",
      "Activity review and traceability",
    ],
    icon: "📡",
  },
  {
    title: "System Hardening",
    desc: "Reduce attack surface across infrastructure, servers, user environments, and connected systems with stronger baseline security practices.",
    points: [
      "Server and environment hardening",
      "Patch and dependency review",
      "Configuration improvement",
      "Exposure reduction",
    ],
    icon: "⚙️",
  },
  {
    title: "Access Control & Identity",
    desc: "Strengthen how users access systems, documents, and workflows through structured permissions and better control over business-critical actions.",
    points: [
      "Role-based access control",
      "Permission structure review",
      "Access policy guidance",
      "Safer user workflow management",
    ],
    icon: "👤",
  },
  {
    title: "Security Review & Consulting",
    desc: "Assess your current environment, identify practical gaps, and define realistic improvements that align with business operations and growth.",
    points: [
      "Risk and control assessment",
      "Security gap identification",
      "Improvement roadmap guidance",
      "Operationally practical recommendations",
    ],
    icon: "📋",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Discover",
    desc: "We study your applications, infrastructure, workflows, and business dependencies to understand where your real risks exist.",
  },
  {
    step: "02",
    title: "Assess",
    desc: "We evaluate vulnerabilities, access patterns, operational gaps, and system exposures that could affect security or continuity.",
  },
  {
    step: "03",
    title: "Implement",
    desc: "We help improve controls, strengthen workflows, and apply security fixes that are practical for your environment.",
  },
  {
    step: "04",
    title: "Monitor",
    desc: "We support ongoing visibility and improvement so security remains aligned with changing business operations.",
  },
];

const industries = [
  {
    title: "Logistics & Freight Operations",
    desc: "Secure shipment workflows, documentation, billing systems, and partner coordination across business-critical processes.",
  },
  {
    title: "Business Applications",
    desc: "Protect internal tools, customer platforms, APIs, and connected workflows from avoidable security weaknesses.",
  },
  {
    title: "Data-Driven Operations",
    desc: "Strengthen how sensitive records, business files, and operational data are stored, accessed, and monitored.",
  },
];

const valuePoints = [
  "Reduce exposure to preventable application and infrastructure risks",
  "Improve security without slowing down essential business workflows",
  "Create stronger control around users, permissions, and sensitive data",
  "Increase visibility into activities, system changes, and suspicious events",
  "Support long-term resilience with practical, scalable improvements",
  "Build a stronger operational foundation for growth and compliance readiness",
];

const Service = () => {
  return (
    <>
      {/* HERO */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-via-slate-900 tw-to-slate-800 tw-text-white tw-py-5">
        <div className="container">
          <div className="row align-items-center tw-min-h-[80vh]">
            <motion.div
              className="col-lg-6 mb-5 mb-lg-0"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.span
                variants={fadeUp}
                className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.25em] tw-text-cyan-300 tw-mb-3"
              >
                Cybersecurity Services
              </motion.span>

              <motion.h1
                className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-3"
                variants={fadeUp}
              >
                Security Services Built for Real Business Operations
              </motion.h1>

              <motion.p
                className="tw-text-slate-300 tw-text-lg tw-mb-4"
                variants={fadeUp}
              >
                Practical cybersecurity solutions designed to protect
                applications, systems, users, and business data with stronger
                operational control and long-term resilience.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="tw-flex tw-flex-wrap tw-gap-3 tw-mb-4"
              >
                <NavLink to="/contact" className="btn btn-primary">
                  Request Consultation
                </NavLink>
                <NavLink to="/products" className="btn btn-outline-light">
                  Explore Product
                </NavLink>
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3"
              >
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Practical Fixes</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Solutions grounded in real operational needs.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Stronger Control</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Better oversight across systems and workflows.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Safer Growth</h6>
                  <p className="tw-text-sm tw-text-slate-300 tw-mb-0">
                    Security that supports scale, not complexity.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <div className="tw-relative">
                <img
                  src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b"
                  alt="Cybersecurity services"
                  className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
                />

                <div className="tw-absolute tw-bottom-4 tw-right-4 tw-bg-white tw-text-slate-900 tw-rounded-2xl tw-shadow-xl tw-p-4 tw-max-w-xs">
                  <h6 className="tw-font-bold tw-mb-1">Operational Security</h6>
                  <p className="tw-text-sm tw-text-slate-600 tw-mb-0">
                    Protect systems, users, and sensitive workflows with
                    business-ready security improvements.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="container tw-py-5">
        <motion.div
          className="row text-center"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {stats.map((item, i) => (
            <motion.div
              key={i}
              className="col-6 col-md-3 mb-4"
              variants={fadeUp}
            >
              <div className="tw-bg-white tw-rounded-3xl tw-p-4 tw-shadow-sm tw-border tw-border-slate-100">
                <h2 className="tw-text-3xl tw-font-bold tw-mb-1">{item.value}</h2>
                <p className="tw-text-slate-600 tw-mb-0">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="container tw-py-5">
        <motion.div
          className="text-center mb-5"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2 className="tw-text-3xl tw-font-semibold">What We Actually Deliver</h2>
          <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
            We focus on practical improvements that reduce risk, strengthen
            access, improve monitoring, and make your systems more resilient
            without disrupting day-to-day operations.
          </p>
        </motion.div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {services.map((service, i) => (
            <motion.div key={i} className="col-md-6 col-lg-4 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 hover:tw-shadow-lg tw-transition-all tw-h-full">
                <div className="tw-text-4xl tw-mb-3">{service.icon}</div>

                <h5 className="tw-font-semibold tw-text-lg tw-mb-2">
                  {service.title}
                </h5>

                <p className="tw-text-slate-600 tw-text-sm tw-mb-3">
                  {service.desc}
                </p>

                <ul className="tw-text-slate-700 tw-text-sm tw-space-y-2 tw-pl-4 tw-mb-0">
                  {service.points.map((point, idx) => (
                    <li key={idx}>• {point}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* PROCESS */}
      <section className="container-fluid tw-bg-slate-50 tw-py-5">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <h2 className="tw-text-3xl tw-font-semibold">Our Security Approach</h2>
            <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
              A structured, practical process that helps teams strengthen
              security step by step with clarity and measurable improvements.
            </p>
          </motion.div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {processSteps.map((item, i) => (
              <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full text-center">
                  <div className="tw-text-cyan-600 tw-font-bold tw-text-2xl tw-mb-2">
                    {item.step}
                  </div>
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-slate-600 tw-text-sm tw-mb-0">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="container tw-py-5">
        <div className="row align-items-center tw-min-h-[500px]">
          <motion.div
            className="col-lg-6 mb-4 mb-lg-0"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <img
              src="https://images.unsplash.com/photo-1555949963-aa79dcee981c"
              alt="Security strategy"
              className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
            />
          </motion.div>

          <motion.div
            className="col-lg-6"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-cyan-600 tw-mb-2">
              Why It Matters
            </span>

            <h2 className="tw-text-3xl tw-font-semibold tw-mb-3">
              Security Is Critical to Reliability, Trust, and Continuity
            </h2>

            <p className="tw-text-slate-600 tw-mb-3">
              Weak security affects more than technology. It creates exposure in
              operations, customer trust, internal workflows, business records,
              and long-term stability.
            </p>

            <p className="tw-text-slate-600 tw-mb-4">
              The right security improvements help organizations reduce avoidable
              disruptions, protect sensitive information, and create more
              dependable systems for teams and customers.
            </p>

            <ul className="tw-text-slate-700 tw-space-y-2 tw-pl-4">
              {valuePoints.map((point, i) => (
                <li key={i}>• {point}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="container-fluid tw-bg-slate-100 tw-py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="tw-text-3xl tw-font-semibold">Where These Services Fit Best</h2>
            <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
              Our services are especially valuable for organizations managing
              sensitive workflows, operational systems, business records, or
              customer-facing applications.
            </p>
          </div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {industries.map((item, i) => (
              <motion.div key={i} className="col-md-4 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-slate-600 tw-mb-0">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-to-slate-800 tw-text-white tw-py-5 text-center">
        <div className="container">
          <h2 className="tw-text-3xl tw-font-semibold">
            Need Help Securing Your Systems and Workflows?
          </h2>
          <p className="tw-text-slate-300 tw-max-w-2xl tw-mx-auto">
            Let’s identify your most important risks and build a stronger,
            practical security foundation for your applications and operations.
          </p>

          <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
            <NavLink to="/contact" className="btn btn-primary">
              Get Started
            </NavLink>
            <NavLink to="/contact" className="btn btn-outline-light">
              Contact Us
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default Service;